import {Injectable} from '@angular/core';
import {Navigation} from '../types/navigation.types';
import {Observable, ReplaySubject, Subscriber} from 'rxjs';
import {FuseNavigationItem} from "../../../@fuse/components/navigation";
import {TranslocoService} from "@ngneat/transloco";
import {cloneDeep} from "lodash-es";
import {PermissionsService} from "./permissions.service";
import {getPermissionMap, SMMFeatEnum} from "../types/permission.types";
import {RecallService} from "../../features/recall/recall.service";

@Injectable({ providedIn: 'root' })
export class NavigationService {

    recallAttentionNeeded:string = null;
    defaultNavigation: FuseNavigationItem[] = [];

    constructor(
        private _translocoService: TranslocoService,
        private _permissionsService: PermissionsService,
        private _recallService: RecallService,

    ) {
        if(_permissionsService.can(SMMFeatEnum.recall_view_clinic)) {
            _recallService.attentionNeeded$.subscribe((attentionNeeded) => {
                if (attentionNeeded > 0) {
                    this.recallAttentionNeeded = attentionNeeded + "";
                } else {
                    this.recallAttentionNeeded = null;
                }

                this.defaultNavigation.forEach(item => {
                    if (item.id == getPermissionMap().get(SMMFeatEnum.recall_view_clinic).id) {
                        if (this.recallAttentionNeeded == null) {
                            delete item.badge;
                        } else {
                            item.badge = {
                                title: this.recallAttentionNeeded,
                                classes: 'bg-red-500 p-2 rounded-full text-white -mt-4'
                            };
                        }
                    }
                })

                let fuseMenu = {
                    compact: cloneDeep(this.defaultNavigation),
                    default: cloneDeep(this.defaultNavigation),
                    futuristic: cloneDeep(this.defaultNavigation),
                    horizontal: cloneDeep(this.defaultNavigation),
                };

                this._navigation.next(fuseMenu);

            })
        }
    }

    private _navigation: ReplaySubject<Navigation> =
        new ReplaySubject<Navigation>(1);


    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------
    /**
     * Getter for navigation
     */
    get navigation$(): Observable<Navigation> {
        return this._navigation.asObservable();
    }

    /**
     * Get all navigation data
     */
    get(): Observable<Navigation> {
        return new Observable((observer:Subscriber<Navigation>) => {

            let defaultNavigation: FuseNavigationItem[] = [];
            if(this._permissionsService.isPlatformOwner()){
                // platform owner nav menu

                defaultNavigation.push( {
                    id: getPermissionMap().get(SMMFeatEnum.core_cl_mgmt).id,
                    title: this._translocoService.translate('Home'),
                    type: 'basic',
                    icon: 'heroicons_outline:home',
                    link: '/platform/dashboard-platform-owner',
                    hidden: this.isPermissionDenied.bind(this)
                });
            }
            else
            if(this._permissionsService.isVisitor()){
                // visitor (patient view)
                defaultNavigation.push( this.getHomeMenuItem());
            }else{
                // clinic staff view
                defaultNavigation.push( this.getPlatformAdminMenuItem());
                const frontDeskOnly = this._permissionsService.isFrontDeskOnly();
                const medicOnly = this._permissionsService.isMedicOnly();
                if(!medicOnly) {
                    defaultNavigation.push(this.getHomeMenuItem());
                }

                const adminItems: FuseNavigationItem[] = [
                    this.getFeatureMgmtMenuItem(),
                    this.getBillingInfoMenuItem(),
                    this.getOrderHistoryMenuItem(),
                    this.getCompanyServicesItem(),
                    this.getFinancialMenuItem(),
                    this.getCompanyInfoItem(),
                ];

                const filteredItems = adminItems.filter(item => !item.hidden(item));
                const anyAdminItem = filteredItems.length > 0;
                if(anyAdminItem) {
                    defaultNavigation.push({
                        id: "ADMIN_GROUP",
                        title: this._translocoService.translate('Admin'),
                        type: 'aside',
                        icon: 'heroicons_outline:cog-6-tooth',
                        children: adminItems,
                    })
                }
                if(!frontDeskOnly) {
                defaultNavigation.push( this.getMedicAgendaItem());
                }
                defaultNavigation.push( this.getCalendarMenuItem());
                if(this._permissionsService.can(SMMFeatEnum.core_team_mgmt_w)){
                    defaultNavigation.push(this.getTeamMenuItem());
                }
                defaultNavigation.push( this.getPatientsMenuItem());
                if(this._permissionsService.can(SMMFeatEnum.recall_view_clinic)){
                    defaultNavigation.push(this.getRecallMenuItem());
                }
                defaultNavigation.push( this.getSterilizationMenuItem());
                defaultNavigation.push( this.getTutorialsMenuItem());
            }

            let fuseMenu = {
                compact: cloneDeep(defaultNavigation),
                default: cloneDeep(defaultNavigation),
                futuristic: cloneDeep(defaultNavigation),
                horizontal: cloneDeep(defaultNavigation),
            };

            this.defaultNavigation = cloneDeep(defaultNavigation);

            this._navigation.next(fuseMenu);
            observer.next( fuseMenu);
            observer.complete();
        });
    }

    private getPlatformAdminMenuItem():FuseNavigationItem {
        return {
            id: getPermissionMap().get(SMMFeatEnum.plf_admin).id,
            title: this._translocoService.translate('Platform admin'),
            type: 'basic',
            icon: 'heroicons_outline:shield-exclamation',
            link: '/platform',
            hidden: this.isPermissionDenied.bind(this)
        };
    }

    private getHomeMenuItem():FuseNavigationItem {
        return {
            id: getPermissionMap().get(SMMFeatEnum.core_cl_mgmt).id,
            title: this._translocoService.translate('Home'),
            type: 'basic',
            icon: 'heroicons_outline:home',
            link: '/home',
            hidden: this.isPermissionDenied.bind(this)
        };
    }

    private getTutorialsMenuItem():FuseNavigationItem {
        return {
            id: getPermissionMap().get(SMMFeatEnum.core_cl_mgmt).id,
            title: this._translocoService.translate('Tutorials'),
            type: 'basic',
            icon: 'heroicons_outline:academic-cap',
            link: '/learning',
            hidden: this.isPermissionDenied.bind(this)
        };
    }

    private getMedicAgendaItem():FuseNavigationItem {
        return {
            id: getPermissionMap().get(SMMFeatEnum.cal_own).id,
            title: this._translocoService.translate('Agenda'),
            type: 'basic',
            icon: 'calendar_view_day',
            link: '/home/dashboard-medic',
            hidden: this.isPermissionDenied.bind(this)
        };
    }


    private getPatientsMenuItem():FuseNavigationItem {
        return {
            id: getPermissionMap().get(SMMFeatEnum.core_cl_mgmt).id,
            title: this._translocoService.translate('Patients'),
            type: 'basic',
            icon: 'heroicons_outline:user-group',
            link: '/patients/list',
            hidden: this.isPermissionDenied.bind(this)
        };
    }

    private getSterilizationMenuItem():FuseNavigationItem {
        return {
            id: getPermissionMap().get(SMMFeatEnum.ster).id,
            title: this._translocoService.translate('Sterilization'),
            type: 'basic',
            icon: 'mat_outline:health_and_safety',
            link: '/sterilization',
            hidden: this.isPermissionDenied.bind(this)
        };
    }

    private getFinancialMenuItem():FuseNavigationItem {
        return {
            id: getPermissionMap().get(SMMFeatEnum.bus_xray).id,
            title: this._translocoService.translate('Business X-ray'),
            type: 'basic',
            icon: 'heroicons_outline:chart-pie',
            link: 'admin/financial',
            hidden: this.isPermissionDenied.bind(this)
        };
    }

    private getFeatureMgmtMenuItem():FuseNavigationItem {
        return {
            id: getPermissionMap().get(SMMFeatEnum.core_mdl_mgmt).id,
            title: this._translocoService.translate('Feature management'),
            type: 'basic',
            // icon: 'heroicons_outline:cube',
            // OR
            icon: 'heroicons_outline:adjustments-horizontal',
            // // OR
            // icon: 'heroicons_outline:cog',
            // // OR
            // icon: 'heroicons_outline:squares-2x2',
            // // OR
            //icon: 'heroicons_outline:building-storefront',
            link: 'admin/feature-mgmt',
            hidden: this.isPermissionDenied.bind(this)
        };
    }

    private getBillingInfoMenuItem():FuseNavigationItem {
        return {
            id: getPermissionMap().get(SMMFeatEnum.core_admin).id,
            title: this._translocoService.translate('Billing info'),
            type: 'basic',
            icon: 'heroicons_outline:credit-card',
            link: 'admin/billing-info',
            hidden: this.isPermissionDenied.bind(this)
        };
    }

    private getOrderHistoryMenuItem():FuseNavigationItem {
        return {
            id: getPermissionMap().get(SMMFeatEnum.core_admin).id,
            title: this._translocoService.translate('Order history'),
            type: 'basic',
            icon: 'mat_solid:history',
            link: 'admin/order-history',
            hidden: this.isPermissionDenied.bind(this)
        };
    }

    private getTeamMenuItem():FuseNavigationItem {
        return {
            id: getPermissionMap().get(SMMFeatEnum.core_team_mgmt_r).id,
            title: this._translocoService.translate('Team'),
            type: 'basic',
            icon: 'heroicons_outline:users',
            link: 'admin/team',
            hidden: this.isPermissionDenied.bind(this)
        };
    }

    private getCompanyInfoItem():FuseNavigationItem {
        return {
            id: getPermissionMap().get(SMMFeatEnum.core_admin).id,
            title: this._translocoService.translate('Company info'),
            type: 'basic',
            icon: 'mat_outline:business',
            link: 'admin/company-info',
            hidden: this.isPermissionDenied.bind(this)
        };
    }

    private getCompanyServicesItem():FuseNavigationItem {
        return {
            id: getPermissionMap().get(SMMFeatEnum.core_admin).id,
            title: this._translocoService.translate('Services and products'),
            type: 'basic',
            icon: 'mat_outline:medical_services',
            link: 'admin/company-services',
            hidden: this.isPermissionDenied.bind(this)
        };
    }

    private getCalendarMenuItem():FuseNavigationItem {
        return {
            id: getPermissionMap().get(SMMFeatEnum.cal_own).id,
            title: this._translocoService.translate('Programări'),
            type: 'basic',
            icon: 'heroicons_outline:calendar-days',
            link: 'calendar',
            hidden: this.isPermissionDenied.bind(this)
        };
    }

    private getRecallMenuItem():FuseNavigationItem {
        const menuEntry:FuseNavigationItem = {
            id: getPermissionMap().get(SMMFeatEnum.recall_view_clinic).id,
            title: this._translocoService.translate('Recalls'),
            type: 'basic',
            icon: 'heroicons_outline:bell-alert',
            link: 'recall',
            hidden: this.isPermissionDenied.bind(this)
        };

        if(this.recallAttentionNeeded){
            menuEntry.badge = {title: this.recallAttentionNeeded, classes: 'bg-red-500 p-2 rounded-full text-white -mt-4'};
        }
        return menuEntry;
    }

    private isPermissionDenied(item: FuseNavigationItem):boolean{
        if(this.notRelevantForPlatformOwner(item)){
            return true;
        }
        return !this._permissionsService.canByID(item.id);
    }

    private isNotMedicAndOwner():boolean{
        return !(this._permissionsService.isMedic() && (this._permissionsService.isTenantOwner() ||
            this._permissionsService.isTenantAdmin()));
    }

    private notRelevantForPlatformOwner(item: FuseNavigationItem){
        return this._permissionsService.isPlatformOwner() && (
            item.link == "calendar" ||
            item.link == "admin/team" ||
            item.link == "/patients/list" ||
            item.link == "admin/financial"
        );
    }


}
